import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import Paginator, { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import PageHeader from "../../components/PageHeader/PageHeader";
import type { KeyboardEvent } from "react";
import GroupApi from "../../services/GroupApi";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import GroupCard from "./components/GroupCard/GroupCard";
import styles from "./index.module.css";

const DEFAULT_COMPANY_ID = "510455ae-d2c1-4e18-a371-5998ff95ea6c";

type GroupSummary = {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  usersCount: number;
  permissionsCount: number;
  category?: string;
  imageUrl?: string | null;
  accentColor?: string | null;
};

type ApiGroupPayload = {
  id?: string;
  groupId?: string;
  groupID?: string;
  Id?: string;
  name?: string;
  groupName?: string;
  description?: string;
  groupDescription?: string;
  isActive?: boolean;
  active?: boolean;
  status?: boolean;
  usersCount?: number | string;
  totalUsers?: number | string;
  permissionsCount?: number | string;
  totalPermissions?: number | string;
  permissions?: unknown[];
  users?: unknown[];
  category?: string;
  type?: string;
  color?: string;
  rulesCount?: number | string;
  rules?: unknown[];
  imageUrl?: string;
  iconUrl?: string;
  icon?: string;
  Description?: string;
};

type FilterState = {
  name: string;
};

const createEmptyFilters = (): FilterState => ({
  name: "",
});

const normalizeFilters = (filters: FilterState): FilterState => {
  const trimmedName = filters.name.trim();
  return {
    name: trimmedName.length >= 3 ? trimmedName : "",
  };
};

const filtersAreEqual = (a: FilterState, b: FilterState): boolean =>
  a.name === b.name;

const FILTER_STORAGE_KEY = "groups:listFilters";
const INITIAL_PAGE_SIZE = 6;
const ROWS_OPTIONS = [6, 12, 24, 36];

const loadFiltersFromStorage = (): FilterState => {
  if (typeof window === "undefined") {
    return createEmptyFilters();
  }

  try {
    const rawValue = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!rawValue) {
      return createEmptyFilters();
    }

    const parsed = JSON.parse(rawValue);
    const name = typeof parsed?.name === "string" ? parsed.name : "";

    return {
      name,
    };
  } catch (error) {
    console.warn("Failed to load groups filters from storage", error);
    return createEmptyFilters();
  }
};

const numberFromUnknown = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (Array.isArray(value)) {
    return value.length;
  }

  return 0;
};

const normalizeGroup = (item: ApiGroupPayload): GroupSummary | null => {
  const id = item.id ?? item.groupId ?? item.groupID ?? item.Id;
  if (!id) {
    return null;
  }

  const name = item.name ?? item.groupName ?? "";
  const description = item.description ?? item.groupDescription ?? item.Description ?? "";
  const isActive =
    typeof item.isActive === "boolean"
      ? item.isActive
      : typeof item.active === "boolean"
        ? item.active
        : typeof item.status === "boolean"
          ? item.status
          : true;

  const usersCount = numberFromUnknown(item.usersCount ?? item.totalUsers ?? item.users);
  const permissionsCount = numberFromUnknown(
    item.permissionsCount ?? item.totalPermissions ?? item.rulesCount ?? item.rules ?? item.permissions,
  );

  const imageUrl = item.imageUrl ?? item.iconUrl ?? item.icon ?? null;
  const accentColor = typeof item.color === "string" && item.color.trim().length > 0 ? item.color : null;

  return {
    id: String(id),
    name: name || String(id),
    description,
    isActive,
    usersCount,
    permissionsCount,
    category: item.category ?? item.type,
    imageUrl,
    accentColor,
  };
};

const iconPool = [
  "pi pi-shield",
  "pi pi-users",
  "pi pi-sitemap",
  "pi pi-lock",
  "pi pi-briefcase",
  "pi pi-cog",
];

const resolveIconClass = (group: GroupSummary, index: number): string => {
  const source = `${group.category ?? ""} ${group.name}`.toLowerCase();

  if (/(admin|seguran)/.test(source)) {
    return "pi pi-shield";
  }

  if (/(gest|manager|coord|lead)/.test(source)) {
    return "pi pi-briefcase";
  }

  if (/(sup|support)/.test(source)) {
    return "pi pi-headphones";
  }

  if (/(viewer|visual)/.test(source)) {
    return "pi pi-eye";
  }

  if (/(auditor|compliance)/.test(source)) {
    return "pi pi-check";
  }

  return iconPool[index % iconPool.length];
};

const GroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { companyId: authCompanyId } = useAuth();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");

  const initialFiltersRef = useRef<FilterState | null>(null);
  if (initialFiltersRef.current === null) {
    initialFiltersRef.current = loadFiltersFromStorage();
  }

  const [allGroups, setAllGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<FilterState>(() => initialFiltersRef.current!);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(() => normalizeFilters(initialFiltersRef.current!));
  const pendingFiltersRef = useRef<FilterState>(pendingFilters);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(INITIAL_PAGE_SIZE);


  const applyFilters = useCallback(
    (nextFilters: FilterState): void => {
      const normalized = normalizeFilters(nextFilters);
      if (filtersAreEqual(appliedFilters, normalized)) {
        return;
      }

      setPage(0);
      setAppliedFilters(normalized);
    },
    [appliedFilters],
  );

  const handleFetchGroups = useCallback(async () => {
    const companyId = authCompanyId?.trim() || DEFAULT_COMPANY_ID;

    try {
      setLoading(true);
      const response = await GroupApi.ListGroups({ companyId });
      const payload = response?.data ?? response;
      const rawList: unknown =
        (payload && typeof payload === "object" && "groups" in payload && (payload as any).groups) ||
        (payload && typeof payload === "object" && "Groups" in payload && (payload as any).Groups) ||
        payload;

      const normalized = (Array.isArray(rawList) ? rawList : []).map((item) =>
        normalizeGroup(item as ApiGroupPayload),
      );

      setAllGroups(normalized.filter((entry): entry is GroupSummary => Boolean(entry)));
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        translateMessages("errorSearch") ||
        "Não foi possível carregar os dados";
      displayToast("error", translateMessages("error") || "Erro", message, false, 4000);
    } finally {
      setLoading(false);
    }
  }, [authCompanyId, displayToast, translateMessages]);

  useEffect(() => {
    handleFetchGroups();
  }, [handleFetchGroups]);

  useEffect(() => {
    pendingFiltersRef.current = pendingFilters;
  }, [pendingFilters]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(pendingFilters));
    } catch (error) {
      console.warn("Failed to persist groups filters", error);
    }
  }, [pendingFilters]);


  const handleApplyFilters = useCallback((): void => {
    applyFilters(pendingFilters);
  }, [applyFilters, pendingFilters]);

  const handleClearFilters = useCallback((): void => {
    const empty = createEmptyFilters();
    setPendingFilters(empty);
    applyFilters(empty);
  }, [applyFilters]);

  const handlePaginatorChange = useCallback((event: PaginatorPageChangeEvent): void => {
    const nextRows = event.rows;
    const nextPage = nextRows > 0 ? Math.floor(event.first / nextRows) : 0;
    setRows(nextRows);
    setPage(nextPage);
  }, []);

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApplyFilters();
    }
  };

  const headingLabel = translateForm("groups") || "Grupos";
  const loadingLabel = translateForm("loading") || "Carregando...";
  const emptyLabel = translateForm("noElementsDefined") || "Nenhum registro encontrado";
  const nameLabel = translateForm("name") || "Nome";
  const usersLabel = translateForm("users") || "Usuários";
  const permissionsLabel = translateForm("permissions") || "Permissões";
  const editLabel = translateForm("edit") || "Editar";
  const managePermissionsLabel = translateForm("permissions") || "Permissões";

  const filteredGroups = useMemo(() => {
    if (allGroups.length === 0) {
      return [] as GroupSummary[];
    }

    const normalized = normalizeFilters(appliedFilters);
    const nameQuery = normalized.name.toLowerCase();

    return allGroups.filter((group) => {
      return (
        !nameQuery ||
        group.name.toLowerCase().includes(nameQuery) ||
        (group.description && group.description.toLowerCase().includes(nameQuery))
      );
    });
  }, [allGroups, appliedFilters]);

  const totalRecords = filteredGroups.length;

  const paginatedGroups = useMemo(() => {
    const start = page * rows;
    return filteredGroups.slice(start, start + rows);
  }, [filteredGroups, page, rows]);

  useEffect(() => {
    const maxPage = rows > 0 ? Math.max(0, Math.ceil(totalRecords / rows) - 1) : 0;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, rows, totalRecords]);

  const cards = useMemo(() => {
    const startIndex = page * rows;
    return paginatedGroups.map((group, index) => (
      <GroupCard
        key={group.id}
        id={group.id}
        name={group.name}
        description={group.description && group.description.trim().length > 0
          ? group.description
          : `${usersLabel}: ${group.usersCount} · ${permissionsLabel}: ${group.permissionsCount}`}
        usersCount={group.usersCount}
        permissionsCount={group.permissionsCount}
        iconClassName={resolveIconClass(group, startIndex + index)}
        imageUrl={group.imageUrl}
        accentColor={group.accentColor ?? undefined}
        usersLabel={usersLabel}
        permissionsLabel={permissionsLabel}
        editLabel={editLabel}
        permissionsButtonLabel={managePermissionsLabel}
        onEdit={(id) => navigate(`/groups/${id}/edit`)}
        onManagePermissions={(id) => navigate(`/groups/${id}/permissions`)}
      />
    ));
  }, [
    editLabel,
    managePermissionsLabel,
    navigate,
    paginatedGroups,
    page,
    permissionsLabel,
    rows,
    usersLabel,
  ]);

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={headingLabel}
        description="Gerencie os grupos de usuários e suas permissões no sistema"
      />

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        <FilterPanel
          title={translateForm("filter") || "Filtros"}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={nameLabel}
            placeholder="Pesquisar por nome ou descrição"
            value={pendingFilters.name}
            onChange={(val) =>
              setPendingFilters((prev) => ({
                ...prev,
                name: val as string,
              }))
            }
            onKeyDown={handleNameKeyDown}
            maxLength={120}
          />
        </FilterPanel>

        <section className={styles.resultsPanel}>
          {loading ? (
            <div className={styles.stateWrapper}>{loadingLabel}</div>
          ) : totalRecords === 0 ? (
            <div className={styles.stateWrapper}>{emptyLabel}</div>
          ) : (
            <div className={styles.grid}>{cards}</div>
          )}

          <div className={styles.paginatorWrapper}>
            <Paginator
              first={page * rows}
              rows={rows}
              totalRecords={totalRecords}
              rowsPerPageOptions={ROWS_OPTIONS}
              onPageChange={handlePaginatorChange}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default GroupsPage;
